import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { Store } from '@generated/tcea/models';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf } from '@angular/common';
import { LoadingComponent } from '@components/loading/1.0/loading.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { ButtonComponent } from '@components/button/1.0/button.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-manage-stores',
  templateUrl: './manage-stores.component.html',
  styleUrls: ['./manage-stores.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    LoadingComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatDialogActions,
    ButtonComponent,
    TranslateModule,
  ],
})
export class ManageStoresComponent implements AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;

  isLoading = false;
  loadingType = LoadingType; // expose enum to template
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  messageType = MessageType; // expose enum to template
  isSaving = false;
  dataSource: MatTableDataSource<Store> = new MatTableDataSource<Store>();
  displayColumns = ['select', 'number', 'name', 'address', 'city'];
  selection = new SelectionModel(true, [], true);
  preSelectedData: { stores: Store[]; selectedStoreIds: string[] };

  constructor(
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<ManageStoresComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.preSelectedData = Object.assign({}, data);
    this.populateData(data);
  }

  private normalizeStoreRow(store: any): Store {
    const normalizedAddress =
      store.address ?? store.storeAddress ?? store.streetAddress?.address1 ?? '';

    return {
      ...store,
      number: store.number ?? store.storeNumber ?? '',
      address: normalizedAddress,
    } as Store;
  }

  populateData(data) {
    this.dataSource.data = (data?.stores || []).map((store: any) => this.normalizeStoreRow(store));
    this.dataSource.data.forEach((store) => {
      if (data.selectedStoreIds?.includes('*') || data.selectedStoreIds?.includes(store.id)) {
        this.selection.select(store);
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  onAdd() {
    this.dialogRef.close({
      selectedStoreList: this.selection.selected,
      isAllSelected: this.isAllSelected(),
    });
  }

  onCancel() {
    this.selection.clear();
    this.populateData(this.preSelectedData);
    this.dialogRef.close(null);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      for (const row of this.dataSource.filteredData) {
        this.selection.select(row);
      }
    }
  }
}
